import { useCurrentUser, useApi } from 'lib/contexts/ApplicationState';
import { useModal } from 'mui-modal-provider';
import { TeamAccount } from 'lib/types/teams';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import { useSnackbar } from 'notistack';

interface ModalProps {
  onClose: () => void;
  onConfirm: () => void;
  teamName: string;
}

const TeamRequestModal = ({ onClose, onConfirm, teamName }: ModalProps) => (
  <Dialog maxWidth="md" open>
    <div className="p-6">
      <div className="relative">
        <h1 className="text-lg text-orange font-bold">Request</h1>
        <div className="absolute right-2 top-2 text-orange">
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </div>
      <DialogContent>
        The
        {' '}
        <strong>
          {teamName}
        </strong>
        {' '}
        team is currently managing this account. Send a request to join the team?
      </DialogContent>
      <DialogActions>
        <>
          <Button onClick={onClose} color="primary">
            Cancel
          </Button>
          <Button onClick={onConfirm} color="inherit">
            Confirm
          </Button>
        </>
      </DialogActions>
    </div>
  </Dialog>
);

interface Props {
  teamAccount: TeamAccount;
}

export const TeamRequestButton = ({ teamAccount }: Props) => {
  const currentUser = useCurrentUser();
  const API = useApi();
  const { showModal } = useModal();
  const { enqueueSnackbar } = useSnackbar();

  const currentAccountToClaim = currentUser?.selfServiceableAccounts?.find(
    (account) => account.id === teamAccount.accountId,
  );

  if (!currentAccountToClaim) {
    // User hasn't authed this account
    return null;
  }

  if (currentUser?.teams?.find((team) => team.id === teamAccount.teamId)) {
    // User is already a member of the team managing this account
    return null;
  }

  const handleClaimClick = async () => {
    const modal = showModal(TeamRequestModal, {
      teamName: teamAccount.team?.name,
      onConfirm: async () => {
        await API.requestToJoinTeam(teamAccount.teamId);
        enqueueSnackbar('Request sent', { variant: 'success' });
        modal.destroy();
      },
      onClose: () => {
        modal.destroy();
      },
    });
  };

  return (
    <button type="button" className="btn-primary" onClick={handleClaimClick}>
      Claim Account
    </button>
  );
};
